import React, { useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/api/client";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { Panel, ActionButton, TextField } from "./_shared";
import { Input } from "@/components/ui/input";
import { KeyRound, ShieldCheck, Eye, EyeOff, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin > Sicurezza
 * - Cambio password amministratore (richiede password attuale)
 * - Cambio rapido PIN per ogni appartamento
 */
export default function SecurityManager() {
  const { me } = useAdminAuth();
  const [apartments, setApartments] = useState([]);
  const [loadingApts, setLoadingApts] = useState(true);
  const [loadError, setLoadError] = useState(false);

  // Admin password change state
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoadingApts(true);
    setLoadError(false);
    api.get("/admin/apartments")
      .then(({ data }) => setApartments(data || []))
      .catch((e) => {
        setLoadError(true);
        toast.error(apiErrorMessage(e));
      })
      .finally(() => setLoadingApts(false));
  }, []);

  const changeAdminPassword = async () => {
    if (!current || !next) return toast.error("Compila tutti i campi");
    if (next.length < 8) return toast.error("La nuova password deve essere di almeno 8 caratteri");
    if (next !== confirm) return toast.error("La conferma non corrisponde");
    if (next === current) return toast.error("La nuova password deve essere diversa da quella attuale");
    setSaving(true);
    try {
      await api.post("/admin/change-password", { current_password: current, new_password: next });
      toast.success("Password aggiornata");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="font-editorial italic text-4xl text-ink-900">Sicurezza</h1>
        <p className="text-xs text-ink-500 mt-1">
          Gestisci la password del pannello e i codici PIN degli appartamenti
        </p>
      </div>

      {/* --- Admin password --- */}
      <Panel title="Password amministratore" testid="security-admin-panel">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3 rounded-xl bg-cream-100 border border-ink-200/40 px-4 py-3">
            <div className="w-9 h-9 rounded-lg bg-white border border-ink-200/40 flex items-center justify-center">
              <ShieldCheck strokeWidth={1.4} className="w-4 h-4 text-corten-500" />
            </div>
            <div className="text-xs text-ink-700">
              Account: <span className="font-semibold">{me?.email}</span>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4 relative">
            <TextField
              label="Password attuale"
              type={showPw ? "text" : "password"}
              value={current}
              onChange={setCurrent}
              testid="admin-pw-current"
            />
            <TextField
              label="Nuova password"
              type={showPw ? "text" : "password"}
              value={next}
              onChange={setNext}
              hint="Almeno 8 caratteri"
              testid="admin-pw-new"
            />
            <TextField
              label="Conferma nuova password"
              type={showPw ? "text" : "password"}
              value={confirm}
              onChange={setConfirm}
              testid="admin-pw-confirm"
            />
          </div>

          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              className="inline-flex items-center gap-1.5 text-xs text-ink-500 hover:text-corten-500 transition"
              data-testid="admin-pw-toggle-visibility"
            >
              {showPw ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              {showPw ? "Nascondi" : "Mostra"}
            </button>
            <ActionButton onClick={changeAdminPassword} disabled={saving} testid="admin-pw-save">
              <Save className="w-3.5 h-3.5" /> {saving ? "Salvataggio..." : "Aggiorna password"}
            </ActionButton>
          </div>
        </div>
      </Panel>

      {/* --- Apartment PINs --- */}
      <Panel title="PIN di accesso appartamenti" testid="security-apts-panel">
        <p className="text-xs text-ink-500 mb-4">
          Ogni ospite riceve un PIN univoco all'arrivo. Cambiarlo qui invalida i nuovi accessi;
          le sessioni già attive restano valide fino alla loro scadenza.
        </p>
        <div className="divide-y divide-ink-200/30">
          {loadingApts && (
            <div className="py-6 flex items-center justify-center gap-2 text-xs text-ink-300" data-testid="pin-loading">
              <Loader2 className="w-4 h-4 animate-spin" /> Caricamento…
            </div>
          )}
          {!loadingApts && loadError && (
            <div className="py-6 text-center text-xs text-red-600" data-testid="pin-load-error">
              Impossibile caricare gli appartamenti.
            </div>
          )}
          {!loadingApts && !loadError && apartments.length === 0 && (
            <div className="py-6 text-center text-xs text-ink-300">Nessun appartamento presente.</div>
          )}
          {!loadingApts && !loadError && apartments.map((a) => (
            <ApartmentPinRow key={a.id} apartment={a} />
          ))}
        </div>
      </Panel>
    </div>
  );
}

function ApartmentPinRow({ apartment }) {
  const [pin, setPin] = useState("");
  const [saving, setSaving] = useState(false);
  const [reveal, setReveal] = useState(false);

  const save = async () => {
    if (!pin) return toast.error("Inserisci un nuovo PIN");
    if (pin.length < 4) return toast.error("Il PIN deve essere di almeno 4 caratteri");
    setSaving(true);
    try {
      await api.patch(`/admin/apartments/${apartment.id}/password`, { password: pin });
      toast.success(`PIN aggiornato per ${apartment.name?.it || apartment.slug}`);
      setPin("");
      setReveal(false);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="py-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`pin-row-${apartment.slug}`}>
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className="w-10 h-10 rounded-xl bg-cream-100 border border-ink-200/40 flex items-center justify-center shrink-0">
          <KeyRound strokeWidth={1.4} className="w-4 h-4 text-corten-500" />
        </div>
        <div className="min-w-0">
          <div className="font-semibold text-sm truncate">{apartment.name?.it || apartment.slug}</div>
          <div className="text-[11px] text-ink-300 truncate">{apartment.slug}</div>
        </div>
      </div>
      <div className="flex items-center gap-2 sm:w-[420px]">
        <div className="relative flex-1">
          <Input
            type={reveal ? "text" : "password"}
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            placeholder="Nuovo PIN"
            data-testid={`pin-input-${apartment.slug}`}
            className="h-10 bg-white border-ink-200/60 focus-visible:ring-corten-400 pr-9"
          />
          <button
            type="button"
            onClick={() => setReveal((v) => !v)}
            data-testid={`pin-reveal-${apartment.slug}`}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-300 hover:text-ink-700"
            aria-label={reveal ? "Nascondi" : "Mostra"}
          >
            {reveal ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        <ActionButton onClick={save} disabled={saving} testid={`pin-save-${apartment.slug}`}>
          <Save className="w-3.5 h-3.5" /> {saving ? "..." : "Salva"}
        </ActionButton>
      </div>
    </div>
  );
}
